import { useDeleteFileQueue } from 'hooks/useDeleteFileQueue'
import { PropsWithChildren, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useSelector } from 'react-redux'
import { composerImagesSelector, composerVideoSelector } from 'services/compose/selectors'
import {
  ACTION_REMOVE_CONTEXT_FILES,
  ACTION_RESET_CONTEXT_FILES,
  ComposerFilesState,
  ComposerMediaContext,
  mediaReducer
} from './context'

export function ComposerMediaContextProvider({ children }: PropsWithChildren<{}>) {
  const images = useSelector(composerImagesSelector)
  const video = useSelector(composerVideoSelector)
  const [files, dispatchFile] = useReducer(mediaReducer, {})
  const deleteUpload = useDeleteFileQueue()

  // EXPL: most callers reset the context in the same handler as the store change that made those
  // files stale, so the reconciliation below would run against an already empty `files` and clean up
  // nothing. Hold on to what a reset wipes until the reconciliation has had a look at it.
  const latestFiles = useRef(files)
  const wipedFiles = useRef<ComposerFilesState>({})
  const contextDispatch = useCallback((action: { type: string, payload?: any }) => {
    if (action.type === ACTION_RESET_CONTEXT_FILES) {
      wipedFiles.current = { ...wipedFiles.current, ...latestFiles.current }
    }
    dispatchFile(action)
  }, [])

  useEffect(() => {
    latestFiles.current = files
  })

  // EXPL: the store drops media without going through removeComposerImages/Video in a few places — a
  // composer reset, or switching media type, where adding images clears the video and vice versa.
  // Anything left holding a File the store no longer knows about is aborted, deleted from the server
  // and dropped, otherwise it leaks and can resurface in a merge. The urls come from the previous
  // commit, since the store entries that carried them are already gone.
  const previousMedia = useRef({ images, video })
  useEffect(() => {
    const { images: previousImages, video: previousVideo } = previousMedia.current
    previousMedia.current = { images, video }

    const candidates = { ...wipedFiles.current, ...files }
    wipedFiles.current = {}

    const staleIds = Object.keys(candidates).filter(id => !images[id] && video?.id !== id)
    if (staleIds.length === 0) {
      return
    }

    staleIds.forEach(id => {
      const file = candidates[id]
      if (!file.upload) {
        return
      }
      file.upload.abort(true)
      deleteUpload(previousVideo?.id === id ? previousVideo.url : previousImages[id]?.url)
    })

    // NOTE: only what the context still holds — the rest is already gone and re-dispatching it would
    // re-run this effect on a state that never changes.
    const idsToDrop = staleIds.filter(id => files[id])
    if (idsToDrop.length > 0) {
      dispatchFile({ type: ACTION_REMOVE_CONTEXT_FILES, payload: idsToDrop })
    }
  }, [images, video, files, deleteUpload])

  // EXPL: a fresh object here would re-render every consumer on each provider render, defeating the
  // memoization in the media tiles. `contextDispatch` is stable, so `files` is the only real input.
  const contextValue = useMemo(() => ({ files, dispatch: contextDispatch }), [files, contextDispatch])

  return (
    <ComposerMediaContext.Provider value={contextValue}>
      {children}
    </ComposerMediaContext.Provider>
  )
}
